/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { ImportMap } from "@sandbox/transpiler";
import { runtimeEnv } from "configuration";
import { PackageMetadata, PackageVersionRegistry, ResolvedPackage } from "types/packageTypes";
import { joinUrl } from "utils/helpers";


const replaceRootPath = (value: string) => joinUrl(runtimeEnv.REACT_APP_SANDBOXURL, runtimeEnv.REACT_APP_BASEURL, "compiler/packages", value);

export interface PackageImportMap {
  packages: ResolvedPackage[],
  importmap: ImportMap;
}

// Define a service using a base URL and expected endpoints
export const packageRegistryApi = createApi({
  reducerPath: "packageRegistryApi",
  baseQuery: fetchBaseQuery({ baseUrl: joinUrl(runtimeEnv.REACT_APP_SANDBOXURL, runtimeEnv.REACT_APP_BASEURL) }),
  endpoints: (builder) => ({
    getPackageVersionRegistry: builder.query<PackageVersionRegistry, void>({
      query: () => `resources/package-registry/package-versions.json`,
    }),
    getPackageMetadata: builder.query<PackageMetadata, { name: string, version: string }>({
      query: ({ name, version }) => `resources/package-registry/${name}-${version}.json`,
    }),
    getImportmap: builder.query<PackageImportMap, string>({
      query: (importMapVersion) => `resources/importmaps/${importMapVersion}`,
      transformResponse: (response: PackageImportMap) => {
        const result: PackageImportMap = {
          packages: [],
          importmap: {
            imports: {}
          }
        };

        const { importmap: { imports, scopes } } = response;

        for (const [key, value] of Object.entries(imports)) {
          result.importmap.imports[key] = replaceRootPath(value);
        }

        if (scopes) {
          result.importmap.scopes = {};
          for (const [key, values] of Object.entries(scopes)) {
            const _key = replaceRootPath(key);

            for (const [dependency, override] of Object.entries(values)) {
              result.importmap.scopes[_key] = result.importmap.scopes[_key] ?? {};
              result.importmap.scopes[_key][dependency] = replaceRootPath(override);
            }
          }
        }

        if (response.packages) {
          for (const pkg of response.packages) {
            pkg.path = replaceRootPath(pkg.path);

            if (pkg.typeDefinitionUrl)
              pkg.typeDefinitionUrl = replaceRootPath(pkg.typeDefinitionUrl);

            result.packages.push(pkg);
          }
        }

        return result;
      }
    })
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetPackageVersionRegistryQuery, useGetPackageMetadataQuery } = packageRegistryApi;
